import { treaty, type Treaty } from '@elysiajs/eden'
import type { App as MainApp } from 'backend-main'
import type { App as AuthApp } from 'backend-auth'
import type { App as AdminApp } from 'backend-admin'
import type { App as ShopApp } from 'backend-shop'
import type { App as StreamerApp } from 'backend-streamer'
import { useAuthStore } from '../stores/auth.store'
import { getActivePinia } from 'pinia'
import { clientEnv } from '../env'

const edenConfig: Treaty.Config = {
  fetch: {
    credentials: 'include',
  },
  fetcher: fetchWithSessionBoundary,
}

async function fetchWithSessionBoundary(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  // SSR session loading is request-scoped in auth.server.ts; never use a
  // process-global active Pinia to mutate another render's session.
  const pinia = typeof window === 'undefined' ? undefined : getActivePinia()
  const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined))
  const usesCapability = headers.has('authorization') || headers.has('x-api-key')
  // Guest/overlay capability failures do not invalidate a browser cookie session.
  const unauthorized = pinia && !usesCapability ? useAuthStore(pinia).captureUnauthorizedHandler() : undefined
  const response = await fetch(input, init)
  if (response.status === 401) unauthorized?.()
  return response
}

// Streamer v2 uses strict JSON-wire contracts whose timestamps are strings.
// Eden otherwise revives ISO strings as Date instances before client-side
// schema validation, making a valid server response fail its declared DTO.
const streamerEdenConfig: Treaty.Config = {
  ...edenConfig,
  parseDate: false,
}

export const api: ReturnType<typeof treaty<MainApp>> = treaty<MainApp>(clientEnv.VITE_API_URL, edenConfig)
export const authApi: ReturnType<typeof treaty<AuthApp>> = treaty<AuthApp>(clientEnv.VITE_AUTH_API_URL, edenConfig)
export const shopApi: ReturnType<typeof treaty<ShopApp>> = treaty<ShopApp>(clientEnv.VITE_SHOP_API_URL, edenConfig)
export const adminApi: ReturnType<typeof treaty<AdminApp>> = treaty<AdminApp>(clientEnv.VITE_ADMIN_API_URL, edenConfig)
export const streamerApi: ReturnType<typeof treaty<StreamerApp>> = treaty<StreamerApp>(clientEnv.VITE_STREAMER_API_URL, streamerEdenConfig)
